import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Phone, User, Play, Square, Camera, Receipt, Package, Check, ChevronLeft, Loader2, Building2 } from "lucide-react";
import { ServiceSteps } from "./ServiceSteps";
import { MaterialRequestButton } from "./MaterialRequestButton";
import { DailyReports } from "./DailyReports";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

// Mobile-first technician view. No category/priority/status editors — those are
// controlled elsewhere. The tech can: check-in/out, tick steps, add notes,
// upload photos and request material. That's it.
export default function TechTicketView({ ticket, onClose, onChange }) {
  const { user } = useAuth();
  const { t } = useLang();
  const [tk, setTk] = useState(ticket);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => setTk(ticket), [ticket]);

  const myOpenEntry = (tk.time_entries || []).find(
    (e) => e.tech_id === user.id && !e.clock_out
  );
  const clockedIn = !!myOpenEntry;

  const reload = async () => {
    try { setTk(await api.get(`/tickets/${tk.id}`)); }
    catch { /* silent */ }
  };

  const doClockIn = async () => {
    setBusy(true);
    try {
      // Try to grab GPS but don't block on it
      await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve({});
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              await api.post(`/tickets/${tk.id}/clock-in`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
              toast.success(t("checkedIn") || "Checked in");
            } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
            resolve();
          },
          async () => {
            try {
              await api.post(`/tickets/${tk.id}/clock-in`, {});
              toast.success(t("checkedIn") || "Checked in");
            } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
            resolve();
          },
          { enableHighAccuracy: false, timeout: 6000 }
        );
      });
      await reload();
      onChange && onChange();
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      await api.post(`/tickets/${tk.id}/clock-out`, {});
      toast.success(t("checkedOut") || "Checked out");
      await reload();
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put(`/tickets/${tk.id}`, { photos: [...(tk.photos || []), reader.result] });
        toast.success(t("photoAdded") || "Photo added");
        await reload();
      } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const address = [tk.property_name, tk.unit_name].filter(Boolean).join(" • ");
  const mapUrl = tk.property_name ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tk.property_name + " " + (tk.address || ""))}` : null;
  const tenantPhone = tk.tenant_contact || tk.contact_phone;

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 overflow-y-auto" data-testid="tech-ticket-view">
      {/* Header — sticky */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 shadow-sm">
        <button data-testid="tech-close" onClick={onClose} className="p-2 -ml-2 rounded-lg hover:bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-slate-400 font-medium">{tk.number}</div>
          <div className="text-base font-bold text-slate-900 truncate">{tk.title}</div>
        </div>
        <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-1 ${
          tk.status === "job_done" ? "bg-emerald-100 text-emerald-700" :
          tk.status === "in_progress" ? "bg-blue-100 text-blue-700" :
          "bg-slate-100 text-slate-600"
        }`}>{(tk.status || "").replace(/_/g, " ")}</span>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto pb-24">
        {/* Location + Contact card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 break-words">{address || "—"}</div>
              {tk.address && <div className="text-xs text-slate-500 mt-0.5 break-words">{tk.address}</div>}
            </div>
            {mapUrl && <a data-testid="tech-open-map" href={mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline shrink-0"><MapPin className="w-3.5 h-3.5" /> {t("route") || "Route"}</a>}
          </div>
          {tk.tenant_name && (
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400 shrink-0" />
              <div className="text-sm text-slate-800 flex-1 truncate">{tk.tenant_name}</div>
              {tenantPhone && <a href={`tel:${tenantPhone}`} data-testid="tech-call" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline shrink-0"><Phone className="w-3.5 h-3.5" /> {tenantPhone}</a>}
            </div>
          )}
        </div>

        {/* Description (read-only) */}
        {tk.description && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">{t("description") || "Description"}</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{tk.description}</p>
          </div>
        )}

        {/* Check-in / Check-out — the big primary action */}
        <button
          data-testid="tech-clock"
          onClick={clockedIn ? doClockOut : doClockIn}
          disabled={busy}
          className={`w-full py-5 rounded-2xl text-lg font-bold flex items-center justify-center gap-3 shadow-sm transition-colors ${
            clockedIn ? "bg-red-500 hover:bg-red-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-white"
          } disabled:opacity-50`}
        >
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (clockedIn ? <Square className="w-6 h-6 fill-white" /> : <Play className="w-6 h-6 fill-white" />)}
          {clockedIn ? (t("checkOut") || "Check out") : (t("checkIn") || "Check in")}
        </button>

        {/* Service Steps */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <ServiceSteps
            ticketId={tk.id}
            steps={tk.service_steps || []}
            canEdit={true}
            onChange={(list) => setTk({ ...tk, service_steps: list })}
          />
        </div>

        {/* Add Note removed — techs now use Daily Reports for all field documentation */}

        {/* Field Records — Material Requests + Daily Reports live together */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 pt-4 pb-3">
            <MaterialRequestButton ticketId={tk.id} />
          </div>
          <div className="px-4 pb-4">
            <DailyReports ticketId={tk.id} />
          </div>
        </div>

        {/* Photos strip + upload */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("photos") || "Photos"} ({(tk.photos || []).length})</div>
            <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 cursor-pointer" data-testid="tech-photo-upload">
              <Camera className="w-4 h-4" /> {uploading ? t("uploading") || "Uploading..." : t("addPhoto") || "Add photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
            </label>
          </div>
          {(tk.photos || []).length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {tk.photos.map((p, i) => <a key={i} href={p} target="_blank" rel="noreferrer" className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200"><img src={p} alt="" className="w-full h-full object-cover" /></a>)}
            </div>
          )}
        </div>

        {/* Timesheet summary for me on this ticket */}
        {(tk.time_entries || []).filter(e => e.tech_id === user.id).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">{t("myTime") || "My time"}</div>
            <ul className="space-y-1 text-sm">
              {tk.time_entries.filter(e => e.tech_id === user.id).map((e, i) => (
                <li key={i} className="flex items-center justify-between text-slate-700">
                  <span>{(e.clock_in || "").slice(0, 16).replace("T", " ")} {e.clock_out ? "→ " + e.clock_out.slice(11, 16) : "→ …"}</span>
                  <span className="font-semibold tabular-nums">{e.hours ? `${e.hours}h` : (e.clock_out ? "" : <span className="text-amber-600 text-xs">running…</span>)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
